/**
 * NABIDKA PROVIZE ZE SMLUV ZAKAZKY (krok P8F8 bod 2).
 *
 * Historie, aby se nevratila: krok UXP5 predvyplnil zaklad provize z
 * NABIDKOVE ceny (`asking_price`), audit 14. 10. to opravil na sjednanou
 * cenu/provizi ze smlouvy — a porad se PREDVYPLNOVALO.
 *
 * R.B. 27. 10. 11:01: provize se MENI (prodá se za jinou cenu, sleva, dohoda),
 * takze ji system NEPOCITA a NEPREDVYPLNUJE. Faktura za provizi ma pole
 * „Provize" prazdne a makler ho vyplni sam. Kdyz zakazka PODEPSANOU
 * zprostredkovatelskou smlouvu se sjednanou provizi ma, formular vedle pole
 * NABIDNE tutez castku („Použít provizi ze smlouvy: 150 000 Kč") a makler ji
 * jednim klepnutim prevezme. Dve podepsane smlouvy = dve nabidky (vyber),
 * koncept = zadna nabidka (rozepsana smlouva by prepsala dohodu).
 *
 * Proto uz tahle funkce NEVRACI „zaklad" — vraci SEZNAM NABIDEK. Zdroj
 * `asking-price` zmizel docela: doklad nabidkovou cenu necte.
 *
 * Ciste funkce bez siti, aby to slo zmerit bez formulare.
 */
import { fieldText } from './contract-presentation.js';
import type { ContractDto } from './contracts-api.js';

export type CommissionOfferSource = 'contract-commission' | 'contract-price';

export interface CommissionOffer {
  readonly contractId: string;
  /** Nazev smlouvy do popisku nabidky („Zprostředkovatelská smlouva"). */
  readonly contractTitle: string;
  /**
   * `contract-commission` = sjednana provize v Kc, dosadi se do pole
   * „Provize" jako pevna castka. `contract-price` = sjednana cena; dosadi se
   * do pomocne kalkulacky „% z ceny" jako zaklad a sazbu zada makler.
   */
  readonly source: CommissionOfferSource;
  /** Kanonicka castka bez mezer (`150000`), uz pripravena pro API. */
  readonly amount: string;
  /**
   * Den podpisu do vety „(smlouva ze dne …)". `ContractDto` datum podpisu
   * nenese; u PODEPSANE smlouvy je `updatedAt` posledni zmenou, a po podpisu
   * se smlouva nemeni — je to tedy den podpisu. ISO datum (`2026-10-21`),
   * formatovani patri do UI.
   */
  readonly signedOn: string | null;
}

/** Castka z pole smlouvy; `null` kdyz pole neni vyplnene nebo to neni cislo. */
function contractAmount(contract: ContractDto, key: string): string | null {
  const text = fieldText(contract.fieldValues, key);
  if (text === null) return null;
  // Smlouva drzi cislo (`150000`), ale rucne zapsana hodnota muze mit mezery
  // tisicu nebo desetinnou carku — doklad potrebuje kanonicky tvar.
  const normalized = text.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;
  const amount = Number(normalized);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return normalized;
}

/** ISO den z casu smlouvy; `null` kdyz cas neni srozumitelny. */
function isoDay(at: string): string | null {
  return /^\d{4}-\d{2}-\d{2}/.test(at) ? at.slice(0, 10) : null;
}

/**
 * Nabidky provize z PODEPSANYCH smluv zakazky, nejnovejsi prvni. Prazdne
 * pole = formular nic nenabidne a makler provizi zada sam (to je vychozi
 * stav, ne chyba).
 *
 * Z jedne smlouvy vznika NEJVYS JEDNA nabidka: sjednana provize prebiji
 * sjednanou cenu, protoze je to presne ta castka, kterou ma doklad uctovat.
 */
export function commissionOffersFromContracts(
  contracts: readonly ContractDto[],
): readonly CommissionOffer[] {
  const signed = contracts.filter((contract) => contract.status === 'signed');
  const sorted = [...signed].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const offers: CommissionOffer[] = [];
  for (const contract of sorted) {
    const commission = contractAmount(contract, 'obchod.provize');
    const base = { contractId: contract.id, contractTitle: contract.title, signedOn: isoDay(contract.updatedAt) };
    if (commission !== null) {
      offers.push({ ...base, source: 'contract-commission', amount: commission });
      continue;
    }
    const price = contractAmount(contract, 'obchod.cena');
    if (price !== null) offers.push({ ...base, source: 'contract-price', amount: price });
  }
  return offers;
}

// --------------------------------------------- varovani u provize (KLIKFIX1 V5)

/**
 * Krok KLIKFIX1 (vada V5) — CO JE NA PROVIZI PODEZRELE.
 *
 * Klikaci test 29. 10. nasel dve veci, ktere faktura udelala BEZ SLOVA:
 *  1. provize prevzata ze smlouvy (450 000 Kč) se na dokladu zvysila o 21 %
 *     DPH na 544 500 Kč, prestoze smlouva o DPH mlci — doklad pak nesedi
 *     s tim, co klient podepsal;
 *  2. do pole „Provize" slo `3,6` (makler myslel procenta) a vznikla faktura
 *     na 4,36 Kč.
 *
 * ROZHODNUTI KLIKFIX1-V5: nic se nepocita ani nepreklapi SAMO (R46 — provizi
 * zadava makler). Formular jen rekne, co vidi; rozhodne makler. Funkce je
 * ciste pravidlo bez formulare a bez site, aby to slo zmerit.
 */
export type CommissionWarning = 'vat-over-contract' | 'amount-too-small';

/**
 * Pod touto castkou (Kc) je provize skoro jiste omylem zapsana procenta.
 * Nejnizsi provize, kterou kancelar fakturuje, je v tisicich; `3,6` ani
 * `450` provize nejsou.
 */
export const COMMISSION_MIN_PLAUSIBLE_CZK = 1000;

export function commissionWarnings(input: {
  readonly commissionType: 'percent' | 'fixed';
  /** Cena pro „% z ceny"; u pevne castky se nepouzije. */
  readonly basisAmount: string;
  readonly commissionValue: string;
  readonly vatRatePercent: string;
  /** Nabidka, kterou makler prevzal ze smlouvy; `null` = zadal cislo sam. */
  readonly takenOffer: { readonly source: CommissionOfferSource } | null;
}): readonly CommissionWarning[] {
  const num = (text: string): number => {
    const value = Number(text.replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(value) ? value : 0;
  };
  const base =
    input.commissionType === 'percent'
      ? num(input.basisAmount) * (num(input.commissionValue) / 100)
      : num(input.commissionValue);
  const out: CommissionWarning[] = [];
  /*
   * DPH nad provizi ze smlouvy. Jen u `contract-commission`: `contract-price`
   * je sjednana CENA do kalkulacky, o provizi (a tedy ani o jeji DPH) nerika
   * nic. Smlouva v dnesnich vzorech pole „provize s DPH" nema, takze „mlci
   * o DPH" plati pro kazdou prevzatou provizi — kdyby takove pole vzniklo,
   * patri podminka sem, na JEDNO misto.
   */
  if (input.takenOffer?.source === 'contract-commission' && num(input.vatRatePercent) > 0) {
    out.push('vat-over-contract');
  }
  if (base > 0 && base < COMMISSION_MIN_PLAUSIBLE_CZK) out.push('amount-too-small');
  return out;
}
